// Natural-language due dates in a task title, Todoist-style: "Call Bob
// tomorrow", "Send deck next friday", "Pay rent every month", "Review Oct 20".
//
// parseDatePhrase(text, options) finds the LAST date phrase in the text and
// returns what it means - it never edits anything; the outline decides what to
// do with it (tasks_outline_controller.js, "Natural-language dates").
//
//   { start, end, phrase, date: "YYYY-MM-DD", time: null | "HH:MM",
//     recurrence: null | { kind, interval?, weekday? },
//     label: "Tomorrow" | "Fri, Oct 17" | "Every Monday" | "Tomorrow 3 PM" }
//
// A time next to the date ("tomorrow 3pm", "at 9:30 friday") rides along and
// becomes the task's reminder; a time on its own ("call at 5pm") means today,
// or tomorrow once that time has passed. Times need am/pm, a colon or "at" -
// a bare number is never a time.
//
// Deliberately conservative - a false positive eats words out of a title:
//   - word boundaries both sides; "today's" is not "today";
//   - month names only WITH a day ("May 5", never "may");
//   - weekday abbreviations only after on / this / next ("sat" alone is a word);
//   - recurrence kinds are exactly what Task supports: daily, weekly (+ weekday),
//     monthly, every_n_days.
// Pure: no DOM, no network; `today` is injectable so tests are deterministic.

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
const WEEKDAY_ABBR = { sun: 0, mon: 1, tue: 2, tues: 2, wed: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6 }
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"]
const MONTH_ABBR = { jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11 }
const SHORT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const SHORT_MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const FULL_WEEKDAY = `(${WEEKDAYS.join("|")})`
const ANY_WEEKDAY = `(${WEEKDAYS.join("|")}|${Object.keys(WEEKDAY_ABBR).join("|")})`
const MONTH_NAME = `(${MONTHS.join("|")}|${Object.keys(MONTH_ABBR).join("|")})`
const ORDINAL = "(\\d{1,2})(?:st|nd|rd|th)?"
const NUMBER_WORDS = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 }
const COUNT = `(\\d{1,3}|${Object.keys(NUMBER_WORDS).join("|")})`

// Each: a pattern and a builder from the match to [hours, minutes] (or null).
const TIME_RULES = [
    [`(?:at\\s+|@\\s*)?(\\d{1,2})(?::([0-5]\\d))?\\s*(am|pm|a\\.m\\.|p\\.m\\.)`, (m) => {
        const h = +m[1]
        if (h < 1 || h > 12) return null
        const pm = m[3].startsWith("p")
        return [(h % 12) + (pm ? 12 : 0), +(m[2] || 0)]
    }],
    [`(?:at\\s+|@\\s*)?([01]?\\d|2[0-3]):([0-5]\\d)`, (m) => [+m[1], +m[2]]],
    // "at 9" - a working-day guess: 1-6 is the afternoon, 7-11 the morning.
    // LOOSE: "look at 3 options" is a number, not a time, so a bare "at N"
    // counts only right next to a date ("friday at 4") or ending the title.
    [`at\\s+(\\d{1,2})`, (m) => {
        const h = +m[1]
        if (h < 1 || h > 23) return null
        return [h >= 1 && h <= 6 ? h + 12 : h, 0]
    }, { loose: true }],
    [`(?:at\\s+)?noon`, () => [12, 0]]
]

// Each rule: a regex over the lower-cased text (no flags needed beyond "g"),
// and a builder from the match to { date, recurrence } (or null to reject).
function rules(today, monthFirst) {
    const at = (offset) => addDays(today, offset)
    return [
        // --- recurrence ---
        [`every\\s+(?:day|morning|evening)|daily`, () => ({ date: today, recurrence: { kind: "daily" } })],
        [`every\\s+${FULL_WEEKDAY}`, (m) => {
            const wd = WEEKDAYS.indexOf(m[1])
            return { date: nextWeekday(today, wd, true), recurrence: { kind: "weekly", weekday: wd } }
        }],
        [`every\\s+week|weekly`, () => ({ date: today, recurrence: { kind: "weekly", weekday: today.getDay() } })],
        [`every\\s+month|monthly`, () => ({ date: today, recurrence: { kind: "monthly" } })],
        [`every\\s+${COUNT}\\s+days?`, (m) => {
            const n = count(m[1])
            if (!n || n > 365) return null
            return { date: today, recurrence: n === 1 ? { kind: "daily" } : { kind: "every_n_days", interval: n } }
        }],
        [`every\\s+${COUNT}\\s+weeks?`, (m) => {
            const n = count(m[1])
            if (!n || n * 7 > 365) return null
            return { date: today, recurrence: n === 1 ? { kind: "weekly", weekday: today.getDay() } : { kind: "every_n_days", interval: n * 7 } }
        }],
        // --- relative ---
        [`today|tonight`, () => ({ date: today })],
        [`tomorrow|tmrw|tmr`, () => ({ date: at(1) })],
        [`next\\s+week`, () => ({ date: nextWeekday(today, 1, false, true) })],
        [`(?:this\\s+)?weekend`, () => ({ date: nextWeekday(today, 6, true) })],
        [`next\\s+month`, () => ({ date: new Date(today.getFullYear(), today.getMonth() + 1, 1) })],
        [`end\\s+of\\s+(?:the\\s+)?month`, () => ({ date: new Date(today.getFullYear(), today.getMonth() + 1, 0) })],
        [`in\\s+${COUNT}\\s+days?`, (m) => (count(m[1]) ? { date: at(count(m[1])) } : null)],
        [`in\\s+${COUNT}\\s+weeks?`, (m) => (count(m[1]) ? { date: at(count(m[1]) * 7) } : null)],
        [`in\\s+${COUNT}\\s+months?`, (m) => (count(m[1]) ? { date: addMonths(today, count(m[1])) } : null)],
        // "next friday" is Friday of NEXT week; "friday" / "this friday" / "on fri"
        // the coming one.
        [`next\\s+${ANY_WEEKDAY}`, (m) => ({ date: nextWeekday(today, weekdayIndex(m[1]), false, true) })],
        [`(?:on|this)\\s+${ANY_WEEKDAY}`, (m) => ({ date: nextWeekday(today, weekdayIndex(m[1]), true) })],
        [FULL_WEEKDAY, (m) => ({ date: nextWeekday(today, WEEKDAYS.indexOf(m[1]), true) })],
        // --- absolute ---
        [`(\\d{4})-(\\d{2})-(\\d{2})`, (m) => validDate(+m[1], +m[2] - 1, +m[3])],
        [`(?:on\\s+)?${MONTH_NAME}\\.?\\s+${ORDINAL}(?:,?\\s+(\\d{4}))?`, (m) => calendarDate(today, monthIndex(m[1]), +m[2], m[3])],
        [`(?:on\\s+)?${ORDINAL}\\s+(?:of\\s+)?${MONTH_NAME}(?:,?\\s+(\\d{4}))?`, (m) => calendarDate(today, monthIndex(m[2]), +m[1], m[3])],
        // 10/20 or 10/20/2026 - month-first for US English, day-first elsewhere.
        [`(\\d{1,2})/(\\d{1,2})(?:/(\\d{2}|\\d{4}))?`, (m) => {
            const [a, b] = [+m[1], +m[2]]
            const [month, day] = monthFirst ? [a, b] : [b, a]
            const year = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : null
            return calendarDate(today, month - 1, day, year)
        }]
    ]
}

export function parseDatePhrase(text, { today = new Date(), now = today, locale = (typeof navigator !== "undefined" ? navigator.language : "en-US") } = {}) {
    const source = String(text ?? "")
    if (!source.trim()) return null
    const lower = source.toLowerCase()
    const base = startOfDay(today)
    const monthFirst = /^en-(us|ph|ca)$/i.test(locale || "")
    let best = null
    for (const [pattern, build] of rules(base, monthFirst)) {
        // Boundary: start or whitespace/"(" before; end, whitespace or
        // sentence punctuation after - so "today's" and "mayday" never match.
        const re = new RegExp(`(^|[\\s(])(?:${pattern})(?=$|[\\s.,;:!?)])`, "g")
        let m
        while ((m = re.exec(lower))) {
            const start = m.index + m[1].length
            const end = m.index + m[0].length
            const inner = new RegExp(`^(?:${pattern})$`).exec(lower.slice(start, end))
            const built = inner && build(inner)
            // The phrase that ENDS last wins, and on a tie the longer one - so
            // "every monday" beats its own "monday", "next friday" its "friday".
            if (built?.date && (!best || end > best.end || (end === best.end && start < best.start))) {
                best = { start, end, ...built }
            }
            re.lastIndex = m.index + 1
        }
    }
    const times = findTimes(lower)
    // A loose time stands alone only at the very end of the title.
    const endsTitle = (t) => /^[\s.,;:!?)]*$/.test(lower.slice(t.end))
    if (best) {
        // A time right before or after the date joins it.
        const joined = times.find((t) => /^\s+$/.test(lower.slice(best.end, t.start)) || /^\s+$/.test(lower.slice(t.end, best.start)))
        if (joined) best = { ...best, start: Math.min(best.start, joined.start), end: Math.max(best.end, joined.end), time: joined.time }
    } else if (times.some((t) => !t.loose || endsTitle(t))) {
        // A time alone: today, or tomorrow once it has gone by.
        const t = times.filter((x) => !x.loose || endsTitle(x)).reduce((a, b) => (b.end > a.end ? b : a))
        const passed = now.getHours() * 60 + now.getMinutes() >= t.time[0] * 60 + t.time[1]
        best = { start: t.start, end: t.end, date: passed ? addDays(base, 1) : base, time: t.time }
    }
    if (!best) return null
    return {
        start: best.start,
        end: best.end,
        phrase: source.slice(best.start, best.end),
        date: iso(best.date),
        time: best.time ? `${String(best.time[0]).padStart(2, "0")}:${String(best.time[1]).padStart(2, "0")}` : null,
        recurrence: best.recurrence || null,
        label: best.time ? `${label(best, base)} ${timeLabel(best.time)}` : label(best, base)
    }
}

function findTimes(lower) {
    const found = []
    for (const [pattern, build, { loose = false } = {}] of TIME_RULES) {
        const re = new RegExp(`(^|[\\s(])(?:${pattern})(?=$|[\\s.,;:!?)])`, "g")
        let m
        while ((m = re.exec(lower))) {
            const start = m.index + m[1].length
            const end = m.index + m[0].length
            const inner = new RegExp(`^(?:${pattern})$`).exec(lower.slice(start, end))
            const time = inner && build(inner)
            // Overlapping rules ("at 9:30" is also "at 9"): the longest wins.
            if (time && !found.some((f) => f.start <= start && f.end >= end)) {
                for (let i = found.length - 1; i >= 0; i--) if (start <= found[i].start && end >= found[i].end) found.splice(i, 1)
                found.push({ start, end, time, loose })
            }
            re.lastIndex = m.index + 1
        }
    }
    return found
}

// "3 PM", "9:30 AM", "12 PM".
function timeLabel([h, m]) {
    const suffix = h >= 12 ? "PM" : "AM"
    const hour = h % 12 || 12
    return m ? `${hour}:${String(m).padStart(2, "0")} ${suffix}` : `${hour} ${suffix}`
}

// The title with the phrase taken out and the spacing tidied.
export function stripDatePhrase(text, match) {
    const before = text.slice(0, match.start).replace(/\s+$/, "")
    const after = text.slice(match.end).replace(/^\s+/, "")
    return [before, after].filter(Boolean).join(" ").replace(/\s+([.,;:!?])/g, "$1").trim()
}

function label({ date, recurrence }, today) {
    if (recurrence) {
        if (recurrence.kind === "daily") return "Every day"
        if (recurrence.kind === "monthly") return "Every month"
        if (recurrence.kind === "every_n_days") return `Every ${recurrence.interval} days`
        return `Every ${capitalize(WEEKDAYS[recurrence.weekday])}`
    }
    const diff = Math.round((startOfDay(date) - today) / 86400000)
    if (diff === 0) return "Today"
    if (diff === 1) return "Tomorrow"
    const sameYear = date.getFullYear() === today.getFullYear()
    return `${SHORT_DAY[date.getDay()]}, ${SHORT_MONTH[date.getMonth()]} ${date.getDate()}${sameYear ? "" : `, ${date.getFullYear()}`}`
}

function count(word) {
    if (/^\d+$/.test(word)) return parseInt(word, 10)
    return NUMBER_WORDS[word] || 0
}

function weekdayIndex(word) {
    const full = WEEKDAYS.indexOf(word)
    return full >= 0 ? full : WEEKDAY_ABBR[word]
}

function monthIndex(word) {
    const full = MONTHS.indexOf(word)
    return full >= 0 ? full : MONTH_ABBR[word]
}

// The next `weekday` after today (today itself counts when includeToday).
// nextWeek: the one in NEXT Monday-to-Sunday week instead.
function nextWeekday(today, weekday, includeToday = false, nextWeek = false) {
    if (nextWeek) {
        const mondayOffset = ((8 - today.getDay()) % 7) || 7
        const nextMonday = addDays(today, mondayOffset)
        return addDays(nextMonday, (weekday + 6) % 7)
    }
    let offset = (weekday - today.getDay() + 7) % 7
    if (offset === 0 && !includeToday) offset = 7
    return addDays(today, offset)
}

// A calendar date with no year means the next one: "Jan 5" in October is next
// January.
function calendarDate(today, month, day, yearText) {
    if (month === undefined || month < 0 || month > 11 || day < 1 || day > 31) return null
    if (yearText) return validDate(+yearText, month, day)
    let date = validDate(today.getFullYear(), month, day)
    if (date && date.date < today) date = validDate(today.getFullYear() + 1, month, day)
    return date
}

function validDate(year, month, day) {
    const date = new Date(year, month, day)
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null
    return { date }
}

function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

function addMonths(date, months) {
    const target = new Date(date.getFullYear(), date.getMonth() + months, 1)
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), last))
}

function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function iso(date) {
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${m}-${d}`
}

function capitalize(word) {
    return word.charAt(0).toUpperCase() + word.slice(1)
}
